// Thin fetch client for the Keel REST API (prototype `web/src/lib/api.ts`), extended with
// refresh-token rotation. Same-origin `/api/v1` — Vite (dev) and nginx (Docker) proxy it.

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export interface Page<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

const BASE = ((import.meta as { env?: Record<string, string> }).env?.VITE_API_URL ?? "").replace(/\/$/, "");

const read = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const write = (k: string, v: string | null) => {
  try {
    v ? localStorage.setItem(k, v) : localStorage.removeItem(k);
  } catch {
    /* private mode */
  }
};

// Security audit finding 7: the access token lives in memory only, the refresh token in an
// httpOnly cookie the page can't read (`X-Keel-Session: cookie`). A non-secret flag remembers that a
// cookie session may exist, so a fresh tab knows to restore it.
let accessToken: string | null = null;
export const session = {
  get token() {
    return accessToken;
  },
  set token(v: string | null) {
    accessToken = v;
  },
  /** A cookie session was started in this browser (not a secret). */
  get signedIn() {
    return read("keel-session") === "1";
  },
  set signedIn(v: boolean) {
    write("keel-session", v ? "1" : null);
  },
  get company() {
    return read("keel-company");
  },
  set company(v: string | null) {
    write("keel-company", v);
  },
};
// Tokens from before cookie sessions must not linger in storage.
write("keel-token", null);
write("keel-refresh", null);

/** Headers that put the auth endpoints in cookie mode. */
export const SESSION_HEADERS = { "X-Keel-Session": "cookie" };

let onUnauthorized: () => void = () => {};
export const setUnauthorizedHandler = (fn: () => void) => {
  onUnauthorized = fn;
};

/**
 * `refused`: the server said no — the session is over. `offline`: no answer at all (the network, or
 * a reload that cancelled the request), which says nothing about the session. Treating that as
 * signed out logged people out whenever they reloaded at the wrong moment (ERP-57).
 */
export type RefreshOutcome = "ok" | "refused" | "offline";

async function refreshOnce(): Promise<RefreshOutcome> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const res = await fetch(`${BASE}/api/v1/auth/refresh`, {
      method: "POST",
      headers: SESSION_HEADERS,
      credentials: "same-origin",
    }).catch(() => null);
    if (!res) return "offline";
    if (res.ok) {
      const t = (await res.json()) as { token: string };
      session.token = t.token;
      return "ok";
    }
    // Another tab rotated the cookie at the same moment: the browser now holds the new one.
    if (res.status === 401 && attempt === 0) await new Promise((r) => setTimeout(r, 400));
    else return res.status === 401 ? "refused" : "offline";
  }
  return "refused";
}

/** One refresh at a time — across tabs too (Web Locks), so a rotated token is never reused. */
let refreshing: Promise<RefreshOutcome> | null = null;
export async function refreshToken(): Promise<boolean> {
  return (await refreshSession()) === "ok";
}
export async function refreshSession(): Promise<RefreshOutcome> {
  if (!session.signedIn) return "refused";
  refreshing ??= (async () => {
    try {
      const locks = (navigator as Navigator & { locks?: LockManager }).locks;
      return locks ? await locks.request("keel-refresh", refreshOnce) : await refreshOnce();
    } finally {
      setTimeout(() => {
        refreshing = null;
      }, 0);
    }
  })();
  return refreshing;
}

/**
 * Asks for a document number typed by hand (ERP-115): a series numbered manually refuses a
 * document without one (`number_required`), and the request goes again with what was typed.
 * The app registers the dialog; `null` is "cancel".
 */
type NumberAsk = (message: string) => Promise<string | null>;
let askNumber: NumberAsk | null = null;
export function setNumberPrompt(fn: NumberAsk | null) {
  askNumber = fn;
}

export async function api<T = unknown>(
  path: string,
  opts: {
    method?: string;
    body?: unknown;
    idempotent?: boolean;
    contentType?: string;
    documentNumber?: string;
  } = {},
  retried = false,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (session.token) headers.Authorization = `Bearer ${session.token}`;
  if (session.company) headers["X-Company-Id"] = session.company;
  // `contentType` sends the body as it is — a bank statement file goes up unchanged, because
  // re-encoding something the bank produced only risks corrupting it.
  if (opts.body !== undefined) headers["Content-Type"] = opts.contentType ?? "application/json";
  if (opts.idempotent) headers["Idempotency-Key"] = crypto.randomUUID();
  if (opts.documentNumber) headers["X-Document-Number"] = encodeURIComponent(opts.documentNumber);
  if (path.startsWith("/auth/")) Object.assign(headers, SESSION_HEADERS);
  const method = opts.method ?? (opts.body !== undefined ? "POST" : "GET");
  const body =
    opts.body === undefined ? undefined : opts.contentType ? String(opts.body) : JSON.stringify(opts.body);
  const res = await fetch(`${BASE}/api/v1${path}`, { method, headers, body });
  const status = res.status;
  if (status === 204) return undefined as T;
  // biome-ignore lint/suspicious/noExplicitAny: problem bodies are untyped JSON
  const data: any = await res.json().catch(() => ({}));
  if (status >= 400) {
    if (
      status === 401 &&
      !path.startsWith("/auth/login") &&
      !path.startsWith("/auth/refresh") &&
      !path.startsWith("/auth/password-reset")
    ) {
      const outcome = retried ? "refused" : await refreshSession();
      if (outcome === "ok") return api<T>(path, opts, true);
      // Only a refusal ends the session; no answer leaves it for the next try.
      if (outcome === "refused") onUnauthorized();
    }
    // Numbered by hand: ask for the number, and again when the one given is taken.
    if (
      askNumber &&
      (data.code === "number_required" || (data.code === "number_taken" && opts.documentNumber))
    ) {
      const given = await askNumber(data.detail || "Give the document's number");
      if (given?.trim()) return api<T>(path, { ...opts, documentNumber: given.trim() }, retried);
    }
    throw new ApiError(data.detail || data.title || `Request failed (${status})`, status, data.code);
  }
  return data as T;
}

export const post = <T = unknown>(path: string, body: unknown = {}) => api<T>(path, { method: "POST", body });
/** GET a response the API produces as a file (a SEPA payment file), with the session's auth. */
export async function getText(path: string): Promise<string> {
  const headers: Record<string, string> = {};
  if (session.token) headers.Authorization = `Bearer ${session.token}`;
  if (session.company) headers["X-Company-Id"] = session.company;
  const res = await fetch(`${BASE}/api/v1${path}`, { headers });
  const text = await res.text();
  if (res.status >= 400) {
    const problem = JSON.parse(text || "{}");
    throw new Error(problem.detail ?? problem.title ?? `Request failed (${res.status})`);
  }
  return text;
}

/**
 * Upload a file (`multipart/form-data`). The Content-Type header is deliberately not set: only the
 * browser knows the boundary it is about to generate, and setting it by hand corrupts the body.
 */
export async function postForm<T = unknown>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = {};
  if (session.token) headers.Authorization = `Bearer ${session.token}`;
  if (session.company) headers["X-Company-Id"] = session.company;
  const res = await fetch(`${BASE}/api/v1${path}`, { method: "POST", headers, body: form });
  const data = await res.json().catch(() => ({}));
  if (res.status >= 400) {
    throw new ApiError(data.detail || data.title || `Upload failed (${res.status})`, res.status, data.code);
  }
  return data as T;
}

/**
 * GET a file the API serves (attachment content). Returns a blob, because the content needs the
 * session's Authorization header and so cannot simply be an `<img src>` or a link.
 */
export async function getBlob(path: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  if (session.token) headers.Authorization = `Bearer ${session.token}`;
  if (session.company) headers["X-Company-Id"] = session.company;
  const res = await fetch(`${BASE}/api/v1${path}`, { headers });
  if (res.status >= 400) {
    const problem = await res.json().catch(() => ({}));
    throw new ApiError(
      problem.detail ?? problem.title ?? `Request failed (${res.status})`,
      res.status,
      problem.code,
    );
  }
  return res.blob();
}

/**
 * POST JSON and take a file back — a document typeset from a template being edited (ERP-78).
 * Resolves to the blob and the response headers (which may say, e.g., which merge fields were blank).
 */
export async function postBlob(path: string, body: unknown): Promise<{ blob: Blob; headers: Headers }> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (session.token) headers.Authorization = `Bearer ${session.token}`;
  if (session.company) headers["X-Company-Id"] = session.company;
  const res = await fetch(`${BASE}/api/v1${path}`, { method: "POST", headers, body: JSON.stringify(body) });
  if (res.status >= 400) {
    const problem = await res.json().catch(() => ({}));
    throw new ApiError(
      problem.detail ?? problem.title ?? `Request failed (${res.status})`,
      res.status,
      problem.code,
    );
  }
  return { blob: await res.blob(), headers: res.headers };
}

/** POST a body that is not JSON, such as a statement file. */
export const postRaw = <T = unknown>(path: string, body: string, contentType: string) =>
  api<T>(path, { method: "POST", body, contentType });
export const put = <T = unknown>(path: string, body: unknown) => api<T>(path, { method: "PUT", body });
/**
 * Resolves to the body, or to `true` for a 204. A delete usually answers with no content, and
 * `useAction().run` reports a failure as `undefined` — so a delete that worked looked exactly like
 * one that failed, and pages waiting to navigate away stayed on a record that was already gone
 * (ERP-11).
 */
export const del = async <T = unknown>(path: string): Promise<T | true> =>
  (await api<T>(path, { method: "DELETE" })) ?? true;

export const qs = (params: Record<string, string | number | boolean | null | undefined>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params))
    if (v !== undefined && v !== null && v !== "") s.set(k, String(v));
  const str = s.toString();
  return str ? `?${str}` : "";
};
